import {
  Tooltip as ShadTooltip,
  TooltipTrigger as ShadTooltipTrigger,
  TooltipContent as ShadTooltipContent,
  TooltipProvider as ShadTooltipProvider,
} from "@/components/ui/tooltip";
import { EnvelopeOpen, Clock, Warning } from "@phosphor-icons/react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import type { CandidateEmailScheduleEntry, EmailScheduleType } from "./types";

const EMAIL_TYPE_LABELS: Record<EmailScheduleType, string> = {
  post_visit_day1: "Dag +1",
  post_visit_day2: "Dag +2",
  post_visit_day3: "Dag +3",
  interested_follow_up: "Interesse opvolging",
};

interface MailIconProps {
  entry: CandidateEmailScheduleEntry;
}

function MailIcon({ entry }: MailIconProps) {
  const label = EMAIL_TYPE_LABELS[entry.emailType] ?? entry.emailType;
  const dateStr = format(new Date(entry.scheduledDate), "d MMM", { locale: nl });

  let icon: React.ReactNode;
  let colorClass: string;
  let tooltipText: string;

  if (entry.status === "sent") {
    icon = <EnvelopeOpen size={14} weight="fill" />;
    colorClass = "text-moss-primary";
    tooltipText = `${label}: verstuurd op ${entry.sentAt ? format(new Date(entry.sentAt), "d MMM HH:mm", { locale: nl }) : dateStr}`;
  } else if (entry.status === "failed") {
    icon = <Warning size={14} weight="fill" />;
    colorClass = "text-red-500";
    tooltipText = `${label}: mislukt${entry.errorMessage ? ` (${entry.errorMessage})` : ""}`;
  } else {
    icon = <Clock size={14} weight="fill" />;
    colorClass = "text-[var(--color-text-muted,#838C89)]";
    tooltipText = `${label}: gepland voor ${dateStr}`;
  }

  return (
    <ShadTooltip>
      <ShadTooltipTrigger asChild>
        <span className={`inline-flex cursor-default ${colorClass}`}>{icon}</span>
      </ShadTooltipTrigger>
      <ShadTooltipContent side="top">
        <p className="text-xs">{tooltipText}</p>
      </ShadTooltipContent>
    </ShadTooltip>
  );
}

interface CandidateMailStatusCellProps {
  schedules: CandidateEmailScheduleEntry[];
}

export function CandidateMailStatusCell({ schedules }: CandidateMailStatusCellProps) {
  if (!schedules || schedules.length === 0) {
    return (
      <span className="text-xs text-[var(--color-text-muted,#838C89)]">–</span>
    );
  }

  return (
    <ShadTooltipProvider delayDuration={200}>
      <div className="flex items-center gap-1">
        {schedules.map((entry) => (
          <MailIcon key={entry.id} entry={entry} />
        ))}
      </div>
    </ShadTooltipProvider>
  );
}
